import { type HTMLAttributes, type ReactNode, forwardRef } from 'react';

import classNames from 'classnames';

import colorStyles from '../Text/colors.module.css';
import styles from './Typography.module.css';

import type { ColorName } from '../../styles';
import type { BreakpointSize } from '../../styles/Breakpoint';
import type { AlignProps } from '../../styles/align';
import { getAlignStyles } from '../../styles/align';
import {
  type SpacingSizeResponsive,
  type SpacingValue,
  getSpacingStyles,
} from '../../styles/spacing';

export type DisplaySize = 21 | 24 | 32 | 40 | 48 | 56 | 72;
export type BodySize = 14 | 16 | 18;
export type AnnotationSize = 18 | 24;
export type UtilitySize = 12 | 14;

export type TypographyVariant = 'display' | 'body' | 'annotation' | 'utility';

export type ResponsiveSize<S extends number> = Partial<
  Record<BreakpointSize, S>
>;

export type TypographyWeight =
  | 'regular'
  | 'medium'
  | 'semibold'
  | 'bold'
  | 'black';

const VARIANT_FONT_CLASS: Record<TypographyVariant, string> = {
  display: 'font-display',
  body: 'font-body',
  annotation: 'font-annotation',
  utility: 'font-utility',
};

const INLINE_ELEMENTS = new Set<TypographyElement>(['span', 'a', 'label']);

const VARIANT_DEFAULT_SIZE: Record<TypographyVariant, number> = {
  display: 32,
  body: 18,
  annotation: 18,
  utility: 14,
};

export type TypographyElement =
  | 'span'
  | 'p'
  | 'div'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'blockquote'
  | 'td'
  | 'tr'
  | 'dt'
  | 'label'
  | 'legend'
  | 'li'
  | 'article'
  | 'figcaption'
  | 'a';

export type VariantShape =
  | { variant?: 'display'; size?: DisplaySize | ResponsiveSize<DisplaySize> }
  | { variant?: 'body'; size?: BodySize | ResponsiveSize<BodySize> }
  | {
      variant?: 'annotation';
      size?: AnnotationSize | ResponsiveSize<AnnotationSize>;
    }
  | { variant?: 'utility'; size?: UtilitySize | ResponsiveSize<UtilitySize> };

export type TypographyProps = VariantShape & {
  /** The HTML element to render. @defaultValue 'span' */
  as?: TypographyElement;
  /** Optional weight override. When omitted, derived from the resolution table. */
  weight?: TypographyWeight;
  /** Text color. */
  color?: ColorName;
  children?: ReactNode;
  m?: SpacingValue | SpacingSizeResponsive;
  mx?: SpacingValue | SpacingSizeResponsive;
  my?: SpacingValue | SpacingSizeResponsive;
  mt?: SpacingValue | SpacingSizeResponsive;
  mb?: SpacingValue | SpacingSizeResponsive;
  ml?: SpacingValue | SpacingSizeResponsive;
  mr?: SpacingValue | SpacingSizeResponsive;
  p?: SpacingValue | SpacingSizeResponsive;
  px?: SpacingValue | SpacingSizeResponsive;
  py?: SpacingValue | SpacingSizeResponsive;
  pt?: SpacingValue | SpacingSizeResponsive;
  pb?: SpacingValue | SpacingSizeResponsive;
  pl?: SpacingValue | SpacingSizeResponsive;
  pr?: SpacingValue | SpacingSizeResponsive;
} & AlignProps &
  Omit<HTMLAttributes<HTMLElement>, 'color'>;

/**
 * Render styled Typography. Responsive sizing is handled entirely via CSS media
 * queries (no JS).
 *
 * @example
 *
 * ```tsx
 * <Typography variant="display" size={72}>Real ingredients</Typography>
 * <Typography variant="body" size={18}>Free shipping on all orders.</Typography>
 * <Typography variant="display" size={{ xs: 40, md: 72 }}>Responsive</Typography>
 *
 * // Ref forwarding
 * const ref = useRef<HTMLElement>(null);
 * <Typography variant="display" size={48} as="h1" ref={ref}>Heading</Typography>
 * ```
 */
export const Typography = forwardRef<HTMLElement | null, TypographyProps>(
  function Typography(props, ref) {
    const {
      variant = 'body',
      size,
      weight,
      as: Component = 'span',
      color = 'Kale3',
      children,
      align,
      m,
      mx,
      my,
      mt,
      mb,
      ml,
      mr,
      p,
      px,
      py,
      pt,
      pb,
      pl,
      pr,
      ...elementProps
    } = props;

    const resolvedSize = size ?? VARIANT_DEFAULT_SIZE[variant];
    const responsiveSize =
      typeof resolvedSize === 'number'
        ? ({ xs: resolvedSize } as Record<string, number>)
        : resolvedSize;

    const sizeClasses: string[] = [];
    for (const [breakpoint, fontSize] of Object.entries(responsiveSize)) {
      const key = `${breakpoint}-${variant}-${fontSize}`;
      if (styles[key]) {
        sizeClasses.push(styles[key]);
      }
    }

    const hasPadding = [p, px, py, pt, pb, pl, pr].some(v => v !== undefined);

    const marginStyles = getSpacingStyles('margin', {
      spacing: m,
      hSpacing: mx,
      vSpacing: my,
      topSpacing: mt,
      bottomSpacing: mb,
      leftSpacing: ml,
      rightSpacing: mr,
    });

    const paddingStyles = getSpacingStyles('padding', {
      spacing: p,
      hSpacing: px,
      vSpacing: py,
      topSpacing: pt,
      bottomSpacing: pb,
      leftSpacing: pl,
      rightSpacing: pr,
    });

    const alignStyles = getAlignStyles(props);

    const className = classNames(
      styles.typography,
      styles[VARIANT_FONT_CLASS[variant]],
      ...sizeClasses,
      weight && styles[`weight-${weight}`],
      color && colorStyles[color],
      hasPadding && INLINE_ELEMENTS.has(Component) && styles['inline-block'],
      alignStyles,
      marginStyles,
      paddingStyles,
      elementProps.className
    );

    const TypographyElement = Component as React.ElementType;

    return (
      <TypographyElement {...elementProps} ref={ref} className={className}>
        {children}
      </TypographyElement>
    );
  }
);
